import { useLeadStatusCreateMutation } from "@/features/dashboard/api/queries/lead-query";
import type { LeadStatus } from "@/features/dashboard/types/lead";
import { IconSelector, IconX } from "@tabler/icons-react";
import clsx from "clsx";
import { useRef, useState } from "react";
import { type ClearIndicatorProps, type DropdownIndicatorProps, type SingleValue, components } from "react-select";
import CreatableSelect from "react-select/creatable";

type Option = { value: string; label: string };

const ClearIndicator = (props: ClearIndicatorProps<Option, false>) => (
	<components.ClearIndicator {...props}>
		<IconX size={18} stroke={1.5} />
	</components.ClearIndicator>
);

const DropdownIndicator = (props: DropdownIndicatorProps<Option, false>) => (
	<components.DropdownIndicator {...props}>
		<IconSelector size={18} stroke={1.5} />
	</components.DropdownIndicator>
);

interface StatusComboboxProps {
	value: string;
	onChange: (value: string) => void;
	statuses: LeadStatus[];
}

export function StatusCombobox({ value, onChange, statuses }: StatusComboboxProps) {
	const createMutation = useLeadStatusCreateMutation();
	const [isCreating, setIsCreating] = useState(false);
	const isUserSelection = useRef(false);

	const options: Option[] = statuses.map((s) => ({ value: s.slug, label: s.name }));
	const selected = options.find((o) => o.value === value) || (value ? { value, label: value } : null);

	const handleChange = (option: SingleValue<Option>) => {
		isUserSelection.current = true;
		onChange(option?.value || "");
	};

	const handleCreate = async (inputValue: string) => {
		setIsCreating(true);
		try {
			const created = await createMutation.mutateAsync({
				name: inputValue,
				color: "gray",
			});
			onChange(created.slug);
		} catch {
			// error handled by mutation
		} finally {
			setIsCreating(false);
		}
	};

	return (
		<CreatableSelect<Option, false>
			options={options}
			value={selected}
			onChange={handleChange}
			onCreateOption={handleCreate}
			isLoading={isCreating}
			isClearable
			placeholder="Select or type new status..."
			formatCreateLabel={(input) => `Create "${input}"`}
			components={{ ClearIndicator, DropdownIndicator }}
			className="w-full text-base"
			classNames={{
				control: ({ isDisabled }) =>
					clsx(
						"flex flex-row w-full min-h-[38px] max-h-[38px] h-[38px] pl-3 pr-0 gap-3 text-base !rounded-md",
						isDisabled
							? "!bg-muted !border-border !text-muted-foreground !cursor-not-allowed"
							: "!bg-background !border-border focus-within:!border-focus-border focus-within:!ring-2 focus-within:!ring-focus-ring/30 !ring-offset-0 !outline-0",
					),
				valueContainer: () => "!p-0",
				placeholder: () => "!text-muted-foreground",
				singleValue: ({ isDisabled }) => clsx(isDisabled ? "!text-muted-foreground" : "!text-foreground"),
				menu: () => "!mt-1 !bg-background !border-1 !border-border !rounded-md !z-[90]",
				option: ({ isFocused, isSelected }) =>
					clsx(
						"!px-3 !py-2 !text-base !text-foreground !cursor-pointer",
						isSelected ? "!bg-blue-100 !text-foreground" : isFocused ? "!bg-blue-50" : "!text-foreground",
					),
				dropdownIndicator: () => "!text-muted-foreground",
				clearIndicator: () => "!text-muted-foreground !cursor-pointer",
			}}
		/>
	);
}
